import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { SpeechService, SpeechTokenResponse } from 'generated-api';
import type * as SDKTypes from 'microsoft-cognitiveservices-speech-sdk';
import { SessionService } from './session.service';

declare global {
  interface Window {
    SpeechSDK: typeof import('microsoft-cognitiveservices-speech-sdk');
  }
}

type Recognizer = SDKTypes.Recognizer;
type ConversationTranscriber = SDKTypes.ConversationTranscriber;
type ConversationTranscriptionEventArgs = SDKTypes.ConversationTranscriptionEventArgs;
type ConversationTranscriptionCanceledEventArgs = SDKTypes.ConversationTranscriptionCanceledEventArgs;
type PushAudioInputStream = SDKTypes.PushAudioInputStream;

function getSpeechSDK() {
  if (!window.SpeechSDK) {
    throw new Error('SpeechSDK is not available. Make sure the Microsoft Cognitive Services Speech SDK script is loaded.');
  }
  return window.SpeechSDK;
}

export const MICROPHONE_ONLY_RECORDING_NOTICE = 'Only microphone will be used.';

export interface RecognitionResult {
  text: string;
  speakerId: string;
  isFinal: boolean;
  recordedAt?: string;
}

export type SpeechTranscriptSegment = {
  text: string;
  speakerId: string;
  recordedAt?: string;
};

export type RecordingStopReason = 'display-capture-ended';

@Injectable({
  providedIn: 'root',
})
export class SpeechRecognitionService {
  private speechService = inject(SpeechService);
  private sessionService = inject(SessionService, { optional: true });
  private recognizer: InstanceType<typeof SDKTypes.ConversationTranscriber> | null = null;
  private tokenExpirationTime: Date | null = null;
  private currentToken: string | null = null;
  private currentRegion: string | null = null;
  private audioContext: AudioContext | null = null;
  private microphoneStream: MediaStream | null = null;
  private displayStream: MediaStream | null = null;
  private pushStream: PushAudioInputStream | null = null;
  private audioWorkletNode: AudioWorkletNode | null = null;
  private alertAudioContext: AudioContext | null = null;
  private releasingCapture = false;
  private requestedStopReason: RecordingStopReason | null = null;
  private completedStopReason: RecordingStopReason | null = null;

  private recognitionSubject = new Subject<RecognitionResult>();
  private isRecordingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new Subject<string>();
  private analyserNode: AnalyserNode | null = null;
  private levelMonitorInterval: ReturnType<typeof setInterval> | null = null;
  /** Poll via interval so the audio level meter keeps working when the tab is backgrounded (rAF pauses). */
  private static readonly LEVEL_POLL_MS = 100;
  /** Boosts mic level for the UI meter; raw peaks are often low for normal speech. */
  private static readonly LEVEL_DISPLAY_GAIN = 8;

  recognition$ = this.recognitionSubject.asObservable();
  isRecording$ = this.isRecordingSubject.asObservable();
  error$ = this.errorSubject.asObservable();

  isRecording = signal(false);
  audioLevel = signal(0);
  interimText = signal<{ text: string; speakerId: string }>({ text: '', speakerId: '' });
  finalText = signal<SpeechTranscriptSegment[]>([]);

  private setRecordingActive(active: boolean): void {
    const wasActive = this.isRecording();
    this.isRecording.set(active);
    this.isRecordingSubject.next(active);
    if (active && !wasActive) {
      this.sessionService?.beginRecordingSessionKeepAlive();
    } else if (!active && wasActive) {
      this.sessionService?.endRecordingSessionKeepAlive();
    }
  }

  isSystemAudioSupported(): boolean {
    return this.isDisplayMediaAudioSupported() && window.isSecureContext;
  }

  private async fetchToken(): Promise<SpeechTokenResponse> {
    const response = await firstValueFrom(
      this.speechService.getToken()
    );
    this.currentToken = response.token || null;
    this.currentRegion = response.region || null;
    this.tokenExpirationTime = new Date(Date.now() + 9 * 60 * 1000);
    return response;
  }

  private async ensureValidToken(): Promise<void> {
    if (
      !this.currentToken ||
      !this.tokenExpirationTime ||
      new Date() >= this.tokenExpirationTime
    ) {
      await this.fetchToken();
    }
  }

  private async refreshTokenIfNeeded(): Promise<void> {
    if (this.tokenExpirationTime && new Date() >= this.tokenExpirationTime) {
      await this.fetchToken();
      if (this.recognizer && this.currentToken) {
        this.recognizer.authorizationToken = this.currentToken;
      }
    }
  }

  private async getWorkletUrl(): Promise<string> {
    const worker = new Worker(
      new URL('./audio-processor.worklet.ts', import.meta.url),
      { type: 'module' }
    );

    return new Promise<string>((resolve, reject) => {
      worker.onmessage = (e) => {
        resolve(e.data);
        worker.terminate();
      };

      worker.onmessageerror = (error) => {
        worker.terminate();
        reject(new Error(`Failed to probe worklet URL: ${error}`));
      };

      worker.onerror = (error) => {
        worker.terminate();
        reject(new Error(`Worker error: ${error.message}`));
      };
    });
  }

  private isDisplayMediaAudioSupported(): boolean {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      return false;
    }
    return true;
  }

  private async captureDisplayAudio(): Promise<MediaStream | null> {
    if (!this.isDisplayMediaAudioSupported()) {
      throw new Error('getDisplayMedia is not supported in this browser');
    }

    if (!window.isSecureContext) {
      throw new Error('getDisplayMedia requires a secure context (HTTPS)');
    }

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        audio: true,
        video: false,
      });

      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        stream.getTracks().forEach(track => track.stop());
        throw new Error('No audio tracks found in display media stream');
      }

      return stream;
    } catch (error) {
      if (error instanceof Error && error.name === 'NotSupportedError') {
        try {
          const streamWithVideo = await navigator.mediaDevices.getDisplayMedia({
            audio: true,
            video: true,
          });

          const videoTracks = streamWithVideo.getVideoTracks();
          videoTracks.forEach(track => track.stop());

          const audioTracks = streamWithVideo.getAudioTracks();
          if (audioTracks.length === 0) {
            streamWithVideo.getTracks().forEach(track => track.stop());
            throw new Error('No audio tracks found in display media stream');
          }

          return streamWithVideo;
        } catch (fallbackError) {
          throw new Error(`Display media audio not supported: ${fallbackError instanceof Error ? fallbackError.message : 'Unknown error'}`);
        }
      }
      throw error;
    }
  }

  private async acquireMicrophoneStream(): Promise<void> {
    try {
      this.microphoneStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
    } catch (error) {
      throw new Error(this.formatMicrophoneAccessError(error));
    }
  }

  private formatMicrophoneAccessError(error: unknown): string {
    if (error instanceof DOMException) {
      if (error.name === 'NotAllowedError') {
        return 'Microphone access was denied. Allow microphone access in your browser site settings, then try again.';
      }
      if (error.name === 'NotFoundError') {
        return 'No microphone was found. Connect a microphone and try again.';
      }
    }
    return error instanceof Error ? error.message : 'Failed to access microphone';
  }

  private async createMixedAudioStream(
    includeSystemAudio: boolean = false
  ): Promise<PushAudioInputStream> {
    const SDK = getSpeechSDK();
    const format = SDK.AudioStreamFormat.getWaveFormatPCM(16000, 16, 1);
    const pushStream = SDK.AudioInputStream.createPushStream(format);
    this.pushStream = pushStream;

    if (!this.microphoneStream) {
      await this.acquireMicrophoneStream();
    }

    const microphoneStream = this.microphoneStream;
    if (!microphoneStream) {
      throw new Error('Failed to access microphone');
    }

    this.audioContext = new AudioContext({ sampleRate: 16000 });

    const workletUrl = await this.getWorkletUrl();
    await this.audioContext.audioWorklet.addModule(workletUrl);

    const microphoneSource = this.audioContext.createMediaStreamSource(microphoneStream);
    const mixer = this.audioContext.createGain();
    microphoneSource.connect(mixer);

    if (includeSystemAudio) {
      try {
        this.displayStream = await this.captureDisplayAudio();

        if (this.displayStream) {
          const displaySource = this.audioContext.createMediaStreamSource(this.displayStream);
          displaySource.connect(mixer);

          this.displayStream.getTracks().forEach(track => {
            track.onended = () => {
              this.stopBecauseDisplayCaptureEnded();
            };
          });
        }
      } catch (error) {
        const errorMessage = error instanceof Error
          ? `Failed to capture system audio: ${error.message}. Only microphone will be used.`
          : 'Failed to capture system audio. Only microphone will be used.';
        console.warn(errorMessage, error);
        this.errorSubject.next(MICROPHONE_ONLY_RECORDING_NOTICE);
      }
    }

    microphoneStream.getTracks().forEach(track => {
      track.onended = () => {
        this.handleMicrophoneCaptureEnded();
      };
    });

    this.audioWorkletNode = new AudioWorkletNode(this.audioContext, 'audio-processor');
    this.audioWorkletNode.port.onmessage = (event) => {
      if (event.data.pcm16) {
        pushStream.write(event.data.pcm16);
      }
    };

    this.analyserNode = this.audioContext.createAnalyser();
    this.analyserNode.fftSize = 256;
    this.analyserNode.smoothingTimeConstant = 0.3;

    const silentDestination = this.audioContext.createGain();
    silentDestination.gain.value = 0;

    mixer.connect(this.audioWorkletNode);
    mixer.connect(this.analyserNode);
    this.analyserNode.connect(silentDestination);
    this.audioWorkletNode.connect(silentDestination);
    silentDestination.connect(this.audioContext.destination);

    this.startAudioLevelMonitoring();

    return pushStream;
  }

  async start(
    options: {
      language?: string,
      includeSystemAudio?: boolean;
    } = {
        language: 'en-US',
        includeSystemAudio: false,
      }
  ): Promise<void> {
    if (this.isRecording()) {
      return;
    }

    this.cleanupAudioResources();
    this.prepareAlertAudio();

    try {
      if (options?.includeSystemAudio) {
        await this.acquireMicrophoneStream();
      }

      await this.ensureValidToken();

      if (!this.currentToken || !this.currentRegion) {
        throw new Error('Failed to obtain speech token');
      }

      const SDK = getSpeechSDK();
      const speechConfig = SDK.SpeechConfig.fromAuthorizationToken(
        this.currentToken,
        this.currentRegion
      );
      speechConfig.speechRecognitionLanguage = options.language || 'en-US';
      speechConfig.setProperty(
        "SpeechServiceResponse_EntityDiarizationEnabled",
        "true"
      );

      let audioConfig: SDKTypes.AudioConfig;

      if (options?.includeSystemAudio) {
        const pushStream = await this.createMixedAudioStream(true);
        audioConfig = SDK.AudioConfig.fromStreamInput(pushStream);
      } else {
        audioConfig = SDK.AudioConfig.fromDefaultMicrophoneInput();
      }

      this.recognizer = new SDK.ConversationTranscriber(speechConfig, audioConfig);

      this.recognizer.transcribing = (_: Recognizer, event: ConversationTranscriptionEventArgs) => {
        this.interimText.set({ text: event.result.text, speakerId: event.result.speakerId });
        this.recognitionSubject.next({
          text: event.result.text,
          speakerId: event.result.speakerId,
          isFinal: false,
        });
      };

      this.recognizer.transcribed = (_: Recognizer, event: ConversationTranscriptionEventArgs) => {
        const SDK = getSpeechSDK();
        if (event.result.reason === SDK.ResultReason.RecognizedSpeech && event.result.text) {
          this.finalText.update((texts) => [
            ...texts,
            {
              text: event.result.text,
              speakerId: event.result.speakerId,
              recordedAt: new Date().toISOString(),
            },
          ]);
          this.interimText.set({ text: '', speakerId: '' });
          this.recognitionSubject.next({
            text: event.result.text,
            speakerId: event.result.speakerId,
            isFinal: true,
          });
        }
      };

      this.recognizer.canceled = (_: Recognizer, event: ConversationTranscriptionCanceledEventArgs) => {
        this.errorSubject.next(event.errorDetails || 'Recognition canceled');
        this.stop();
      };

      this.recognizer.sessionStopped = () => {
        this.setRecordingActive(false);
      };

      this.recognizer.startTranscribingAsync(
        () => {
          this.setRecordingActive(true);
          this.startTokenRefreshInterval();
        },
        (error: any) => {
          this.errorSubject.next(error);
          this.setRecordingActive(false);
        }
      );
    } catch (error) {
      this.cleanupAudioResources();
      const message = error instanceof Error ? error.message : 'Failed to start recognition';
      this.errorSubject.next(message);
      throw error instanceof Error ? error : new Error(message);
    }
  }

  private tokenRefreshInterval: ReturnType<typeof setInterval> | null = null;

  private startTokenRefreshInterval(): void {
    this.stopTokenRefreshInterval();
    this.tokenRefreshInterval = setInterval(
      () => {
        this.refreshTokenIfNeeded();
      },
      8 * 60 * 1000
    );
  }

  private stopTokenRefreshInterval(): void {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }
  }

  stopBecauseDisplayCaptureEnded(): void {
    if (this.releasingCapture || !this.isRecording()) {
      return;
    }

    this.requestedStopReason = 'display-capture-ended';
    this.playDisplayCaptureEndedBeep();
    void this.stop();
  }

  private prepareAlertAudio(): void {
    try {
      if (!this.alertAudioContext || this.alertAudioContext.state === 'closed') {
        this.alertAudioContext = new AudioContext();
      }
      void this.alertAudioContext.resume();
    } catch {
      this.alertAudioContext = null;
    }
  }

  private playDisplayCaptureEndedBeep(): void {
    const ctx = this.alertAudioContext;
    if (!ctx || ctx.state === 'closed') {
      return;
    }

    const play = () => this.scheduleDisplayCaptureEndedBeeps(ctx);
    if (ctx.state === 'suspended') {
      void ctx.resume().then(play).catch(() => undefined);
      return;
    }
    play();
  }

  private scheduleDisplayCaptureEndedBeeps(ctx: AudioContext): void {
    const now = ctx.currentTime;
    this.scheduleBeep(ctx, 880, now, 0.16);
    const last = this.scheduleBeep(ctx, 880, now + 0.26, 0.16);
    last.onended = () => {
      if (this.alertAudioContext !== ctx) {
        return;
      }
      this.alertAudioContext = null;
      void ctx.close();
    };
  }

  private scheduleBeep(ctx: AudioContext, frequency: number, when: number, duration: number): OscillatorNode {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, when);
    gain.gain.setValueAtTime(0.0001, when);
    gain.gain.exponentialRampToValueAtTime(0.35, when + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, when + duration);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(when);
    oscillator.stop(when + duration);
    return oscillator;
  }

  takeUnexpectedStopReason(): RecordingStopReason | null {
    const reason = this.completedStopReason ?? this.requestedStopReason;
    this.completedStopReason = null;
    this.requestedStopReason = null;
    return reason;
  }

  stop(): Promise<{ text: string; speakerId: string }[]> {
    if (this.releasingCapture) {
      return Promise.resolve(this.finalText());
    }

    this.releasingCapture = true;
    this.stopTokenRefreshInterval();

    return new Promise((resolve) => {
      const finish = () => {
        this.cleanupAudioResources();
        this.completedStopReason = this.requestedStopReason;
        this.requestedStopReason = null;
        this.setRecordingActive(false);
        this.releasingCapture = false;
        resolve(this.finalText());
      };

      if (!this.recognizer) {
        finish();
        return;
      }

      this.recognizer.stopTranscribingAsync(
        () => {
          this.recognizer?.close();
          this.recognizer = null;
          finish();
        },
        (error: any) => {
          this.errorSubject.next(error);
          this.recognizer?.close();
          this.recognizer = null;
          finish();
        }
      );
    });
  }

  private handleMicrophoneCaptureEnded(): void {
    if (this.releasingCapture || !this.isRecording()) {
      return;
    }

    this.errorSubject.next('Microphone was disconnected or access was revoked.');
    void this.stop();
  }

  private startAudioLevelMonitoring(): void {
    this.stopAudioLevelMonitoring();

    if (!this.analyserNode) return;

    this.levelMonitorInterval = setInterval(() => {
      this.pollAudioLevel();
    }, SpeechRecognitionService.LEVEL_POLL_MS);
  }

  private pollAudioLevel(): void {
    const analyser = this.analyserNode;
    if (!analyser) {
      this.audioLevel.set(0);
      return;
    }

    const dataArray = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(dataArray);

    let peak = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const amplitude = Math.abs(dataArray[i] - 128);
      if (amplitude > peak) {
        peak = amplitude;
      }
    }
    const raw = peak / 128;
    const normalized = Math.min(raw * SpeechRecognitionService.LEVEL_DISPLAY_GAIN, 1);
    this.audioLevel.set(normalized);
  }

  private stopAudioLevelMonitoring(): void {
    if (this.levelMonitorInterval !== null) {
      clearInterval(this.levelMonitorInterval);
      this.levelMonitorInterval = null;
    }
    this.audioLevel.set(0);
  }

  private cleanupAudioResources(): void {
    this.stopAudioLevelMonitoring();

    if (this.analyserNode) {
      this.analyserNode.disconnect();
      this.analyserNode = null;
    }

    if (this.audioWorkletNode) {
      this.audioWorkletNode.disconnect();
      this.audioWorkletNode.port.close();
      this.audioWorkletNode = null;
    }

    if (this.pushStream) {
      this.pushStream.close();
      this.pushStream = null;
    }

    if (this.microphoneStream) {
      this.microphoneStream.getTracks().forEach(track => track.stop());
      this.microphoneStream = null;
    }

    if (this.displayStream) {
      this.displayStream.getTracks().forEach(track => track.stop());
      this.displayStream = null;
    }

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }
  }

  reset(): void {
    this.interimText.set({ text: '', speakerId: '' });
    this.finalText.set([]);
  }

  getFullTranscript(): string {
    const segments = this.finalText();
    const interim = this.interimText();

    const grouped: { speakerId: string; texts: string[] }[] = [];
    for (const segment of segments) {
      const last = grouped[grouped.length - 1];
      if (last && last.speakerId === segment.speakerId) {
        last.texts.push(segment.text);
      } else {
        grouped.push({ speakerId: segment.speakerId, texts: [segment.text] });
      }
    }

    const formatted = grouped.map(g => `${g.speakerId}: ${g.texts.join(' ')}`).join('\n\n');

    if (interim.text) {
      return `${formatted}\n\n${interim.speakerId}: ${interim.text}`.trim();
    }
    return formatted;
  }
}

